import gettext from '@sdk/lang/gettext';
import { SmartTooltip } from '@sdk/common/tooltip/SmartTooltip';
import BodyPortal from '@sdk/common/tooltip/BodyPortal';
import useModalState from '@sdk/components/ui/modal/useModalState';

import { MessagePost } from '@web/conversations/types/Message';
import { DeleteConfirmModal } from '@web/components/DeleteConfirmModal/DeleteConfirmModal';
import { ReactionButton } from '@web/conversations/Reactions/ReactionButton/ReactionButton';
import { ReactionPositionedSelect } from '@web/conversations/Reactions/ReactionPositionedSelect/ReactionPositionedSelect';

import { useMessageContextMenu, useEmojiPicker } from './messageContextMenu.hooks';
import {
  Button,
  PencilIcon,
  ReplyIcon,
  RingbellIcon,
  TrashIcon,
  Wrapper,
} from './messageContextMenu.styles';

type Props = {
  isRestricted?: boolean;
  lockMenu: () => void;
  message: MessagePost;
  unlockMenu: () => void;
};

export const MessageContextMenu = ({
  isRestricted = false,
  lockMenu,
  message,
  unlockMenu,
}: Props) => {
  const { canReact, markAsUnread, remove, replyTo, selectReaction, openEdit } =
    useMessageContextMenu(message);

  const deleteConfirmationModalState = useModalState();

  const {
    isOn: isEmojiPickerOpen,
    onPick,
    position: pickerPosition,
    ref,
    toggle: toggleEmojiPicker,
  } = useEmojiPicker({
    lockMenu,
    onSelect: selectReaction,
    unlockMenu,
  });

  return (
    <Wrapper ref={ref}>
      {canReact && (
        <>
          <ReactionButton handleClick={toggleEmojiPicker} isActive={isEmojiPickerOpen} />
          {isEmojiPickerOpen && (
            <ReactionPositionedSelect handleSelect={onPick} position={pickerPosition} />
          )}
        </>
      )}

      {!isRestricted && (
        <SmartTooltip content={gettext('Reply')}>
          <Button onClick={replyTo}>
            <ReplyIcon />
          </Button>
        </SmartTooltip>
      )}

      <SmartTooltip content={gettext('Mark unread')}>
        <Button onClick={markAsUnread}>
          <RingbellIcon />
        </Button>
      </SmartTooltip>

      {!isRestricted && message.canEdit && (
        <SmartTooltip content={gettext('Edit')}>
          <Button onClick={openEdit}>
            <PencilIcon />
          </Button>
        </SmartTooltip>
      )}

      {message.canDelete && (
        <>
          <SmartTooltip content={gettext('Delete')}>
            <Button onClick={deleteConfirmationModalState.open}>
              <TrashIcon />
            </Button>
          </SmartTooltip>

          <BodyPortal>
            <DeleteConfirmModal
              modalState={deleteConfirmationModalState}
              onSubmit={remove}
              title={gettext('Delete message')}
              description={gettext('Are you sure you want to delete this message?')}
              primaryButtonText={gettext('Delete')}
            />
          </BodyPortal>
        </>
      )}
    </Wrapper>
  );
};
